import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Snackbar,
  CircularProgress
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';
import { useMutation, gql } from '@apollo/client';

const DELETE_DEALER_MUTATION = gql`
mutation DeleteDealer($dealerId: String!) {
  deleteDealer(dealerId: $dealerId) {
    status
    message
  }
}
`;

interface DeleteDealersDialogProps {
  open: boolean;
  onClose: () => void;
  dealer: {
    dealer_id?: string;
    dealer_name?: string;
    [key: string]: any;
  };
  onDeleted?: () => void;
}

const DeleteDealersDialog: React.FC<DeleteDealersDialogProps> = ({ open, onClose, dealer, onDeleted }) => {
  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  const [deleteDealer, { loading }] = useMutation(DELETE_DEALER_MUTATION);

  const handleDelete = async () => {
    if (!dealer?.dealer_id) return;
    try {
      await deleteDealer({
        variables: { dealerId: dealer.dealer_id },
        update(cache) {
          cache.evict({ id: `Dealer:${dealer.dealer_id}` });
          cache.gc();
        }
      });
      setSnackbar({ message: 'Dealer deleted successfully', severity: 'success' });
      onDeleted?.();
      onClose();
    } catch (err) {
      const errorMessage =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message?: unknown }).message)
          : 'An error occurred';
      setSnackbar({ message: errorMessage, severity: 'error' });
    }
  };

  const handleCloseSnackbar = () => setSnackbar(null);

  return (
    <>
      <Dialog open={open} onClose={onClose}>
        <DialogTitle sx={{ pt: '5%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Are you sure you want to delete this dealer?</span>
          <IconButton aria-label="close" onClick={onClose} size="small" sx={{ color: 'inherit', right: -15, top: -15 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogActions sx={{ p:'5px',pb: '5%', px: '3%' ,justifyContent: 'center',gap:'10px'}}>
          <Button onClick={onClose} variant="outlined" >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <MuiAlert
          onClose={handleCloseSnackbar}
          severity={snackbar?.severity}
          sx={{ width: '100%' }}
          elevation={6}
          variant="filled"
        >
          {snackbar?.message}
        </MuiAlert>
      </Snackbar>
    </>
  );
};

export default DeleteDealersDialog;
